import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import Flatpickr from 'react-flatpickr';
import MaskedInput from 'react-text-mask';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { DebounceInput } from 'react-debounce-input';

import {
  Alert, Button, Checkbox, Drawer, InfoCircle, Select, Tabs, TextInput, Toggle, Tooltip,
} from '@opendate-app/setlist';

import CurrencyInput from '../../CurrencyInput/components/CurrencyInput';
import DynamicPricingSection from './DynamicPricingSection';
import PriceBreakoutAccordion from './PriceBreakoutAccordion';
import TicketPricePoints from './TicketPricePoints';
import PriceOptionsEditor from './PriceOptionsEditor';
import { showsPriceOptions } from '../utils/priceOptions';
import {
  ACCESSIBLE_TICKET_OPTIONS,
  calculateLift,
  canSubmitForm,
  fairMarketValueError,
  formatter,
  goodsServicesDescriptionError,
  manualTaxShouldBeChecked,
  minTicketsPerOrderMoreThanMax,
  moveFocusWithoutSelectingPriceType,
  parseDateTime,
  showActivityOption,
  visiblePricePoints,
} from './ticketTypeFormHelpers';

import './TicketDrawer.css';
import './TicketTypeDrawer.css';

const TICKET_TYPE_DRAWER_FORM_ID = 'ticket-type-drawer-form';

const PRICE_TYPE_TABS = [
  { value: 'paid', label: 'Paid' },
  { value: 'free', label: 'Free' },
];

const TIMED_ENTRY_AVAILABILITY_OPTIONS = [
  { value: 'Based on show details', label: 'Based on show details' },
  { value: 'Date & Time', label: 'Date & Time' },
];

const CHECK_IN_PERIOD_OPTIONS = [
  { value: 'all_time', label: 'All Time' },
  { value: 'daily', label: 'Daily' },
];

const VISIBILITY_OPTIONS = [
  { value: 'Visible', label: 'Visible' },
  { value: 'Hidden', label: 'Hidden' },
  { value: 'Hidden when not on sale', label: 'Hidden when not on sale' },
];

const SALES_CHANNEL_OPTIONS = [
  { value: 'Everywhere', label: 'Everywhere' },
  { value: 'Online only', label: 'Online only' },
  { value: 'At the door only', label: 'At the door only' },
];

const RequiredLabel = ({ children }) => (
  <>
    {children}
    <span className="ticket-drawer-required">&nbsp;*</span>
  </>
);

const LabelWithInfo = ({ tip, children }) => (
  <span className="ticket-drawer-label-with-info">
    {children}
    <Tooltip title={tip} maxWidth={240}>
      <span className="ticket-drawer-info" tabIndex={0} aria-label="More information">
        <InfoCircle width={14} height={14} aria-hidden="true" />
      </span>
    </Tooltip>
  </span>
);

const TicketTypeDrawer = ({
  csrfToken,
  team,
  confirm,
  manualTaxes,
  preSaleEnabled,
  showActivities,
  ticketTypes,
  ticketTypeForForm,
  ticketTypeForFormChanged,
  ticketTypeIsSeated,
  exampleTicketChanged,
  createTicketType,
  updateTicketType,
  distributionTermsAccepted,
  enablePricePoints,
  newPricePoint,
  pricePointsMaxDate,
  pricePointsMinDate,
  setPricePointToRemove,
  setShowDistributionTermsModal,
  startShowActivity,
  useDateAndTime,
  useShowDetails,
  onAfterOpen,
  debouncedDetermineFinalCosts,
  overCapacity = null,
  raisingCapacity = false,
  onKeepCapacity = () => {},
  closeOnEsc = true,
}) => {
  const fairMarketValueEnabled = Boolean(team?.can_access_fair_market_value);
  const saveTicketType = (raiseCapacity) => {
    if (ticketTypeForForm.id) {
      updateTicketType(csrfToken, team, ticketTypeForForm, raiseCapacity);
    } else {
      createTicketType(csrfToken, team, confirm, ticketTypeForForm, raiseCapacity);
    }
  };

  const isOpen = Object.keys(ticketTypeForForm).length > 0;
  const showPricePointColumns = Boolean(enablePricePoints && visiblePricePoints(ticketTypeForForm?.price_points).length);
  const otherTicketTypeOptions = ticketTypes
    .filter((tt) => tt.id !== ticketTypeForForm.id)
    .map((ticketType) => ({ value: String(ticketType.id), label: ticketType.base_name }));
  const showActivityOptions = showActivities.map(showActivity => showActivityOption(showActivity));

  useEffect(() => {
    if (isOpen && onAfterOpen) {
      onAfterOpen();
    }
  }, [isOpen]);

  return (
    <Drawer
      open={isOpen}
      backdrop
      closeOnEsc={closeOnEsc}
      width={600}
      title={ticketTypeForForm.id ? 'Edit Ticket' : 'Add Ticket'}
      data-lift-conductor-orb={isOpen || undefined}
      onClose={() => {
        ticketTypeForFormChanged({});
        exampleTicketChanged({});
      }}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => ticketTypeForFormChanged({})}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            form={TICKET_TYPE_DRAWER_FORM_ID}
            disabled={!canSubmitForm(ticketTypeForForm, fairMarketValueEnabled) || raisingCapacity}
          >
            Save
          </Button>
        </>
      }
    >
      <form id={TICKET_TYPE_DRAWER_FORM_ID}
            className="ticket-type-drawer"
            onSubmit={
              (e) => {
                e.preventDefault();

                if(!canSubmitForm(ticketTypeForForm, fairMarketValueEnabled)){
                  return false;
                }

                saveTicketType(false);
              }
            }>
        {overCapacity !== null ? (
          <Alert
            color="warning"
            role="alert"
            title={`This would put the show over its capacity of ${overCapacity}. Raise capacity too?`}
            actions={
              <>
                <Button variant="secondary" size="sm" disabled={raisingCapacity} onClick={onKeepCapacity}>Cancel</Button>
                <Button variant="primary" size="sm" disabled={raisingCapacity} onClick={() => saveTicketType(true)}>Raise capacity</Button>
              </>
            }
          />
        ) : null}
        <div onKeyDownCapture={moveFocusWithoutSelectingPriceType}>
          <Tabs
            variant="button-border"
            size="sm"
            fullWidth
            collapseBelow={0}
            aria-label="Ticket price type"
            items={PRICE_TYPE_TABS}
            value={ticketTypeForForm.free ? 'free' : 'paid'}
            onChange={(priceType) => {
              var updated = priceType === 'free' ? (
                Object.assign({}, ticketTypeForForm, {
                  free: true,
                  base_price_two_decimals: "0.00",
                  settlement_amount_two_decimals: "0.00",
                  has_fixed_fees: false,
                  fixed_fee_amount_two_decimals: "",
                  absorb_fees: false,
                  dynamic_pricing_enabled: false
                })
              ) : (
                Object.assign({}, ticketTypeForForm, {free: false})
              );

              ticketTypeForFormChanged(updated);
              debouncedDetermineFinalCosts(updated);
            }}
          />
        </div>
        <TextInput
          id="ticket-type-name"
          label={<RequiredLabel>Name</RequiredLabel>}
          disabled={ticketTypeIsSeated(ticketTypeForForm)}
          value={ticketTypeForForm.base_name || ""}
          onChange={
            (e) => {
              var updated = Object.assign({}, ticketTypeForForm, {base_name: e.target.value});
              ticketTypeForFormChanged(updated);
            }
          }
        />
        <TextInput
          as={MaskedInput}
          id="ticket-type-available"
          label={<RequiredLabel>Available Quantity</RequiredLabel>}
          mask={createNumberMask({
            prefix: "",
            decimalLimit: 0
          })}
          disabled={ticketTypeIsSeated(ticketTypeForForm)}
          onChange={
            (e) => {
              var updated = Object.assign({}, ticketTypeForForm, {available: e.target.value});
              ticketTypeForFormChanged(updated);
            }
          }
          value={ticketTypeForForm.available || ""}
        />
        {ticketTypeForForm.free ? (
          <TextInput
            id="ticket-type-price"
            label={<RequiredLabel>Price</RequiredLabel>}
            leadingText="$"
            value="Free"
            disabled
          />
        ) : (
          <TextInput
            as={CurrencyInput}
            id="ticket-type-price"
            label={<RequiredLabel>Price</RequiredLabel>}
            leadingText="$"
            maskOptions={{
              prefix: "",
              integerLimit: null
            }}
            value={ticketTypeForForm.base_price_two_decimals || ""}
            onChange={
              (e) => {
                var amount = e.target.value;

                var updated = Object.assign({}, ticketTypeForForm, {
                  base_price_two_decimals: amount,
                  settlement_amount_two_decimals: amount
                });

                ticketTypeForFormChanged(updated);
                debouncedDetermineFinalCosts(updated);
              }
            }
          />
        )}
        <DynamicPricingSection
          ticketTypeForForm={ticketTypeForForm}
          onTicketTypeChange={ticketTypeForFormChanged}
          team={team}
        />
        {!ticketTypeForForm.free ? (
          <PriceBreakoutAccordion
            item={ticketTypeForForm}
            dynamicPricingInfo={(() => {
              if (!ticketTypeForForm.dynamic_pricing_enabled || !ticketTypeForForm.base_price_two_decimals) {
                return null;
              }

              const basePrice = parseFloat(ticketTypeForForm.base_price_two_decimals) || 0;
              const minPrice = parseFloat(ticketTypeForForm.dynamic_pricing_min) || 0;
              const maxPrice = parseFloat(ticketTypeForForm.dynamic_pricing_max) || 0;
              const cashFinalCost = parseFloat(ticketTypeForForm.cash_final_cost) || 0;
              const cardFinalCost = parseFloat(ticketTypeForForm.final_cost) || 0;
              const cashTaxes = parseFloat(ticketTypeForForm.cash_taxes) || 0;
              const cardTaxes = parseFloat(ticketTypeForForm.taxes) || 0;

              if (basePrice > 0 && (cashFinalCost > 0 || cardFinalCost > 0)) {
                const minPercentage = ((minPrice - basePrice) / basePrice * 100);
                const maxPercentage = ((maxPrice - basePrice) / basePrice * 100);

                const cashTotal = cashFinalCost + cashTaxes;
                const cardTotal = cardFinalCost + cardTaxes;

                let cashMin = Math.max(0, cashTotal + (cashTotal * minPercentage / 100));
                let cashMax = cashTotal + (cashTotal * maxPercentage / 100);
                let cardMin = Math.max(0, cardTotal + (cardTotal * minPercentage / 100));
                let cardMax = cardTotal + (cardTotal * maxPercentage / 100);

                // If round up is enabled, round the max values to nearest dollar
                if (ticketTypeForForm.round_up) {
                  cashMin = Math.ceil(cashMin);
                  cashMax = Math.ceil(cashMax);
                  cardMin = Math.ceil(cardMin);
                  cardMax = Math.ceil(cardMax);
                }

                return { cashMin, cashMax, cardMin, cardMax };
              }
              return null;
            })()}
          />
        ) : null}
        {!ticketTypeForForm.free ? (
          <Checkbox
            id="ticket-type-round-up"
            label="Round Up: Increase the organizer fee to round the final cost to the nearest dollar"
            checked={ticketTypeForForm.round_up || false}
            onChange={
              (e) => {
                var updated = Object.assign({}, ticketTypeForForm, {
                  round_up: e.target.checked,
                });

                ticketTypeForFormChanged(updated);
                debouncedDetermineFinalCosts(updated);
              }
            }
          />
        ) : null}
        {!ticketTypeForForm.free ? (
          <Checkbox
            id="ticket-type-absorb-fees"
            label="Absorb Fees: Ticketing fees are deducted from your ticket revenue"
            checked={ticketTypeForForm.absorb_fees || false}
            onChange={
              (e) => {
                var updated = Object.assign({}, ticketTypeForForm, {
                  absorb_fees: e.target.checked
                });

                ticketTypeForFormChanged(updated);
                debouncedDetermineFinalCosts(updated);
              }
            }
          />
        ) : null}
        {ticketTypeForForm.allow_fixed_fees && !ticketTypeForForm.free ? (
          <>
            <Checkbox
              id="ticket-type-has-fixed-fees"
              label="Fixed Fees"
              checked={ticketTypeForForm.has_fixed_fees || false}
              onChange={
                (e) => {
                  var updated = Object.assign({}, ticketTypeForForm, {
                    has_fixed_fees: e.target.checked,
                    fixed_fee_amount_two_decimals: ""
                  });

                  ticketTypeForFormChanged(updated);
                  debouncedDetermineFinalCosts(updated);
                }
              }
            />
            {ticketTypeForForm.has_fixed_fees ? (
              <TextInput
                as={CurrencyInput}
                id="ticket-type-fixed-fee-amount"
                label="Fee Amount"
                leadingText="$"
                maskOptions={{
                  prefix: "",
                  integerLimit: null
                }}
                value={ticketTypeForForm.fixed_fee_amount_two_decimals || ""}
                onChange={
                  (e) => {
                    var updated = Object.assign({}, ticketTypeForForm, {fixed_fee_amount_two_decimals: e.target.value});

                    ticketTypeForFormChanged(updated);
                    debouncedDetermineFinalCosts(updated);
                  }
                }
              />
            ) : null}
          </>
        ) : null}
        {showsPriceOptions(ticketTypeForForm, ticketTypeIsSeated(ticketTypeForForm), confirm?.ticketing_arrangement === 'Seated') ? (
          <PriceOptionsEditor ticketType={ticketTypeForForm} onChange={ticketTypeForFormChanged} teamId={team?.id} />
        ) : null}
        {!ticketTypeIsSeated(ticketTypeForForm) ? (
          <>
            <hr className="ticket-type-drawer-divider" />
            <div>
              <Checkbox
                id="ticket-type-has-bundled-ticket-type"
                label={
                  <LabelWithInfo tip="Inventory from the linked ticket type will not be automatically adjusted when a purchase is made of this bundle. Please adjust available inventory as required.">
                    This is a bundle that includes an existing ticket type.
                  </LabelWithInfo>
                }
                checked={ticketTypeForForm.has_bundled_ticket_type || false}
                onChange={
                  (e) => {
                    var updated = Object.assign({}, ticketTypeForForm, {
                      has_bundled_ticket_type: e.target.checked,
                      bundled_ticket_type_id: ""
                    });

                    ticketTypeForFormChanged(updated);
                  }
                }
              />
              {ticketTypeForForm.has_bundled_ticket_type ? (
                <div className="ticket-drawer-bundle">
                  <Select
                    id="ticket-type-bundled-ticket-type-id"
                    aria-label="Bundled ticket type"
                    placeholder="Select Ticket Type"
                    clearable
                    options={otherTicketTypeOptions}
                    value={ticketTypeForForm.bundled_ticket_type_id ? String(ticketTypeForForm.bundled_ticket_type_id) : null}
                    onChange={(ticketTypeId) => {
                      var updated = Object.assign({}, ticketTypeForForm, {
                        bundled_ticket_type_id: ticketTypeId || ""
                      });

                      ticketTypeForFormChanged(updated);
                    }}
                  />
                  {ticketTypeForForm.bundled_ticket_type_id ? (
                    <p className="ticket-drawer-muted-text">
                      Artist Lift: {formatter.format(calculateLift(ticketTypeForForm, ticketTypes))}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          </>
        ) : null}
        <p className="ticket-drawer-block-title">Sales Start</p>
        <div>
          <label htmlFor="ticket-type-availability" className="ticket-drawer-field-label">
            When are tickets available?
          </label>
          <select className="form-control form-control-slim ticket-type-drawer-native-select"
                  value={ticketTypeForForm.availability || ""}
                  onChange={(e) => {
                    var updated;

                    if(e.target.value === "Date & Time" && ticketTypeForForm.availability != e.target.value) {
                      updated = Object.assign({}, ticketTypeForForm, {
                        availability: e.target.value,
                      });
                    } else {
                      updated = Object.assign({}, ticketTypeForForm, {availability: e.target.value});
                    }

                    ticketTypeForFormChanged(updated);
                  }}
                  id="ticket-type-availability">
            <option value="Based on show details">
              Based on show details
            </option>
            <option value="Date & Time">
              Date & Time
            </option>
            <option value="When sales end for...">
              When ticket sales end for...
            </option>
            <option value="When tickets sell out for...">
              When tickets sell out for...
            </option>
            <option value="When ticket sales end or sell out for...">
              When ticket sales end or sell out for...
            </option>
          </select>
        </div>
        <div className="ticket-drawer-field-row">
          <div className="ticket-drawer-field">
            {useDateAndTime ? (
              <div>
                <label htmlFor="ticket-type-start-date" className="ticket-drawer-field-label">
                  <RequiredLabel>Start Date</RequiredLabel>
                </label>
                <Flatpickr
                  id="ticket-type-start-date"
                  options={{
                    dateFormat: "M j, Y at h:iK",
                    enableTime: true,
                  }}
                  placeholder={"Select date and time"}
                  className={"form-control ticketing-date-picker form-control-flatpickr"}
                  style={{fontSize: "14px"}}
                  value={ticketTypeForForm.start_time}
                  onClose={dates => {
                    var startTime = parseDateTime(dates[0]);
                    var endTime   = parseDateTime(ticketTypeForForm.end_time);

                    var attrs = {
                      start_time: startTime,
                    };

                    if(endTime && endTime < startTime){
                      endTime = "";
                      attrs = Object.assign({}, attrs, {
                        end_time: endTime
                      });
                    }

                    var updated = Object.assign({}, ticketTypeForForm, attrs);
                    ticketTypeForFormChanged(updated);
                  }}
                />
              </div>
            ) : useShowDetails ? (
              <>
                <Select
                  id="ticket-type-start-date"
                  label={<RequiredLabel>Start Show Detail</RequiredLabel>}
                  placeholder="Select a show detail"
                  options={showActivityOptions}
                  value={ticketTypeForForm.start_show_activity_id ?? null}
                  onChange={showActivityId => {
                    ticketTypeForFormChanged({
                      ...ticketTypeForForm,
                      start_show_activity_id: showActivityId
                    });
                  }}
                />
                {preSaleEnabled && startShowActivity?.name === "Venue Pre-Sale" ? (
                  <Alert
                    color="gray"
                    description="Linking to the Venue Pre-Sale show detail and adding a promo code for this ticket will trigger the presale automated email campaign."
                  />
                ) : null}
              </>
            ) : (
              <Select
                id="ticket-type-wait-for-ticket-type"
                label="Ticket Type"
                placeholder="Select Ticket Type"
                clearable
                options={otherTicketTypeOptions}
                value={ticketTypeForForm.wait_for_ticket_type_id ? String(ticketTypeForForm.wait_for_ticket_type_id) : null}
                onChange={ticketTypeId => {
                  var updated = Object.assign({}, ticketTypeForForm, {wait_for_ticket_type_id: ticketTypeId || ""});
                  ticketTypeForFormChanged(updated);
                }}
              />
            )}
          </div>
          {showPricePointColumns ? (
            <div className="ticket-drawer-field ticket-type-drawer-price-point-price">
              <TextInput
                as={CurrencyInput}
                id="add-on-price"
                label={<LabelWithInfo tip="Based on the price field above">Price</LabelWithInfo>}
                leadingText="$"
                disabled
                maskOptions={{prefix: ""}}
                value={ticketTypeForForm.base_price_two_decimals || ""}
                hint={ticketTypeForForm.final_cost ? (
                  <>
                    Final: {formatter.format(parseFloat(ticketTypeForForm.final_cost))}
                    {ticketTypeForForm.dynamic_pricing_enabled && (() => {
                      const basePrice = parseFloat(ticketTypeForForm.base_price_two_decimals) || 0;
                      const minPrice = parseFloat(ticketTypeForForm.dynamic_pricing_min) || 0;
                      const maxPrice = parseFloat(ticketTypeForForm.dynamic_pricing_max) || 0;
                      const cardFinalCost = parseFloat(ticketTypeForForm.final_cost) || 0;
                      const cardTaxes = parseFloat(ticketTypeForForm.taxes) || 0;

                      if (basePrice > 0 && cardFinalCost > 0) {
                        const minPercentage = ((minPrice - basePrice) / basePrice * 100);
                        const maxPercentage = ((maxPrice - basePrice) / basePrice * 100);
                        const cardTotal = cardFinalCost + cardTaxes;

                        let cardMin = Math.max(0, cardTotal + (cardTotal * minPercentage / 100));
                        let cardMax = cardTotal + (cardTotal * maxPercentage / 100);

                        if (ticketTypeForForm.round_up) {
                          cardMin = Math.ceil(cardMin);
                          cardMax = Math.ceil(cardMax);
                        }

                        return (
                          <span className="ticket-type-drawer-dynamic-range">
                            ⚡ ${cardMin.toFixed(2)}-${cardMax.toFixed(2)}
                          </span>
                        );
                      }
                      return null;
                    })()}
                  </>
                ) : null}
              />
            </div>
          ) : null}
        </div>
        {enablePricePoints ? (
          <TicketPricePoints
            ticketType={ticketTypeForForm}
            onChange={ticketTypeForFormChanged}
            onRecalculate={debouncedDetermineFinalCosts}
            onAdd={newPricePoint}
            onRemove={setPricePointToRemove}
            minDate={pricePointsMinDate}
            maxDate={pricePointsMaxDate}
          />
        ) : null}
        {!useShowDetails ? (
          <div>
            <label htmlFor="ticket-type-end-date" className="ticket-drawer-field-label">
              <RequiredLabel>End Date</RequiredLabel>
            </label>
            <Flatpickr
              id="ticket-type-end-date"
              options={{
                dateFormat: "M j, Y at h:iK",
                enableTime: true,
              }}
              placeholder={"Select date & Time"}
              className={"form-control ticketing-date-picker form-control-flatpickr"}
              style={{fontSize: "14px"}}
              value={ticketTypeForForm.end_time}
              onClose={dates => {
                var attrs = {
                  end_time: parseDateTime(dates[0])
                };

                var updated = Object.assign({}, ticketTypeForForm, attrs);
                ticketTypeForFormChanged(updated);
              }}
            />
          </div>
        ) : (
          <Select
            id="ticket-type-end-date"
            label={<RequiredLabel>End Show Detail</RequiredLabel>}
            placeholder="Select a show detail"
            options={showActivityOptions}
            value={ticketTypeForForm.end_show_activity_id ?? null}
            onChange={showActivityId => {
              ticketTypeForFormChanged({
                ...ticketTypeForForm,
                end_show_activity_id: showActivityId
              });
            }}
          />
        )}
        <Toggle
          id="ticket-type-timed-entry"
          label="Timed Entry"
          checked={ticketTypeForForm.timed_entry || false}
          onChange={e => {
            ticketTypeForFormChanged({
              ...ticketTypeForForm,
              timed_entry: e.target.checked
            });
          }}
        />
        {ticketTypeForForm.timed_entry ? (
          <>
            <Select
              id="ticket-type-timed-entry-availability"
              label="When can tickets be scanned?"
              options={TIMED_ENTRY_AVAILABILITY_OPTIONS}
              value={ticketTypeForForm.timed_entry_availability || TIMED_ENTRY_AVAILABILITY_OPTIONS[0].value}
              onChange={timedEntryAvailability => {
                ticketTypeForFormChanged({
                  ...ticketTypeForForm,
                  timed_entry_availability: timedEntryAvailability
                });
              }}
            />
            {ticketTypeForForm.timed_entry_availability === "Date & Time" ? (
              <>
                <div>
                  <label htmlFor="ticket-type-timed-entry-start-date" className="ticket-drawer-field-label">
                    <RequiredLabel>Scan Start Time</RequiredLabel>
                  </label>
                  <Flatpickr
                    id="ticket-type-timed-entry-start-date"
                    options={{
                      dateFormat: "M j, Y at h:iK",
                      enableTime: true,
                    }}
                    placeholder={"Select date and time"}
                    className={"form-control ticketing-date-picker form-control-flatpickr"}
                    style={{fontSize: "14px"}}
                    value={ticketTypeForForm.timed_entry_start}
                    onClose={dates => {
                      var startTime = parseDateTime(dates[0]);
                      var endTime   = parseDateTime(ticketTypeForForm.timed_entry_end);

                      var attrs = {
                        timed_entry_start: startTime,
                      };

                      if(endTime && endTime < startTime){
                        endTime = "";
                        attrs = {
                          ...attrs,
                          timed_entry_end: endTime
                        };
                      }

                      ticketTypeForFormChanged({
                        ...ticketTypeForForm,
                        ...attrs
                      });
                    }}
                  />
                </div>
                <div>
                  <label htmlFor="ticket-type-timed-entry-end-date" className="ticket-drawer-field-label">
                    <RequiredLabel>Scan End Time</RequiredLabel>
                  </label>
                  <Flatpickr
                    id="ticket-type-timed-entry-end-date"
                    options={{
                      dateFormat: "M j, Y at h:iK",
                      enableTime: true,
                    }}
                    placeholder={"Select date and time"}
                    className={"form-control ticketing-date-picker form-control-flatpickr"}
                    style={{fontSize: "14px"}}
                    value={ticketTypeForForm.timed_entry_end}
                    onClose={dates => {
                      ticketTypeForFormChanged({
                        ...ticketTypeForForm,
                        timed_entry_end: parseDateTime(dates[0])
                      });
                    }}
                  />
                </div>
              </>
            ) : ticketTypeForForm.timed_entry_availability == "Based on show details" ? (
              <>
                <Select
                  id="ticket-type-timed-entry-start-show-activity"
                  label={<RequiredLabel>Scan Start Show Detail</RequiredLabel>}
                  placeholder="Select a show detail"
                  options={showActivityOptions}
                  value={ticketTypeForForm.timed_entry_start_show_activity_id ?? null}
                  onChange={showActivityId => {
                    ticketTypeForFormChanged({
                      ...ticketTypeForForm,
                      timed_entry_start_show_activity_id: showActivityId
                    });
                  }}
                />
                <Select
                  id="ticket-type-timed-entry-end-show-activity"
                  label={<RequiredLabel>Scan Stop Show Detail</RequiredLabel>}
                  placeholder="Select a show detail"
                  options={showActivityOptions}
                  value={ticketTypeForForm.timed_entry_end_show_activity_id ?? null}
                  onChange={showActivityId => {
                    ticketTypeForFormChanged({
                      ...ticketTypeForForm,
                      timed_entry_end_show_activity_id: showActivityId
                    });
                  }}
                />
              </>
            ) : null}
          </>
        ) : null}
        <hr className="ticket-type-drawer-divider" />
        <p className="ticket-drawer-block-title">Advanced Settings</p>
        <TextInput
          as={MaskedInput}
          id="ticket-type-check-in-limit"
          label="How many times can a single ticket be scanned?"
          hint="Set to 0 for unlimited scans"
          mask={createNumberMask({ prefix: "", decimalLimit: 0 })}
          value={ticketTypeForForm.check_in_limit || "0"}
          onChange={e => {
            ticketTypeForFormChanged({
              ...ticketTypeForForm,
              check_in_limit: e.target.value
            });
          }}
        />
        <Select
          id="ticket-type-check-in-period"
          label="Check-in Period"
          hint={'"Daily" resets the check-in count each day at 4 AM venue local time.'}
          options={CHECK_IN_PERIOD_OPTIONS}
          value={ticketTypeForForm.check_in_period || "all_time"}
          onChange={checkInPeriod =>
            ticketTypeForFormChanged({
              ...ticketTypeForForm,
              check_in_period: checkInPeriod,
            })
          }
        />
        <TextInput
          as={DebounceInput}
          element="textarea"
          multiline
          rows={5}
          resize="none"
          id="ticket-type-description"
          label="Description"
          placeholder="Tell attendees more about this ticket..."
          value={ticketTypeForForm.description || ""}
          onChange={
            (e) => {
              var updated = Object.assign({}, ticketTypeForForm, {description: e.target.value});
              ticketTypeForFormChanged(updated);
            }
          }
        />
        <div>
          <Select
            id="ticket-type-visibility"
            label="Visibility"
            options={VISIBILITY_OPTIONS}
            value={ticketTypeForForm.original_visibility || VISIBILITY_OPTIONS[0].value}
            onChange={visibility => {
              var updated = Object.assign({}, ticketTypeForForm, {original_visibility: visibility});
              ticketTypeForFormChanged(updated);
            }}
          />
          {ticketTypeForForm.visibility === "Hidden" && ticketTypeForForm.original_visibility !== "Hidden" ? (
            <div className="mt-2">
              <Alert
                color="warning"
                title="Visibility Override"
                description="This ticket is currently hidden due to an active promo code. You can still edit the original visibility setting above to determine what visibility will be once the promo code is no longer active."
              />
            </div>
          ) : null}
        </div>
        <p className="ticket-drawer-block-title">Tickets Per Order</p>
        <div className="ticket-drawer-field-row">
          <div className="ticket-drawer-field">
            <TextInput
              as={MaskedInput}
              id="ticket-type-minimum-tickets-per-order"
              label="Minimum"
              error={minTicketsPerOrderMoreThanMax(ticketTypeForForm) || undefined}
              mask={createNumberMask({
                prefix: "",
                decimalLimit: 0
              })}
              onChange={
                (e) => {
                  var updated = Object.assign({}, ticketTypeForForm, {minimum_tickets_per_order: e.target.value});
                  ticketTypeForFormChanged(updated);
                }
              }
              value={ticketTypeForForm.minimum_tickets_per_order || ""}
            />
          </div>
          <div className="ticket-drawer-field">
            <TextInput
              as={MaskedInput}
              id="ticket-type-maximum-tickets-per-order"
              label="Maximum"
              error={minTicketsPerOrderMoreThanMax(ticketTypeForForm) || undefined}
              mask={createNumberMask({
                prefix: "",
                decimalLimit: 0
              })}
              onChange={
                (e) => {
                  var updated = Object.assign({}, ticketTypeForForm, {maximum_tickets_per_order: e.target.value});
                  ticketTypeForFormChanged(updated);
                }
              }
              value={ticketTypeForForm.maximum_tickets_per_order || ""}
            />
          </div>
        </div>
        <Select
          id="ticket-type-sales-channel"
          label="Sales Channel"
          disabled={ticketTypeIsSeated(ticketTypeForForm)}
          hint={ticketTypeIsSeated(ticketTypeForForm) ? "Staff can still sell these seats at the door from Add Attendees or the Organizer app." : undefined}
          options={SALES_CHANNEL_OPTIONS}
          value={ticketTypeForForm.sales_channel || SALES_CHANNEL_OPTIONS[0].value}
          onChange={salesChannel => {
            var updated = Object.assign({}, ticketTypeForForm, {sales_channel: salesChannel});
            ticketTypeForFormChanged(updated);
          }}
        />
        {!ticketTypeForForm.free ? (
          <>
            <p className="ticket-drawer-block-title">Settlement Amount</p>
            <TextInput
              as={CurrencyInput}
              id="ticket-type-settlement-amount"
              label="Customize the settlement amount"
              leadingText="$"
              error={(ticketTypeForForm.price_two_decimals && !ticketTypeForForm.settlement_amount_two_decimals) || undefined}
              maskOptions={{
                prefix: "",
                integerLimit: null
              }}
              value={ticketTypeForForm.settlement_amount_two_decimals || ""}
              onChange={
                (e) => {
                  var updated = Object.assign({}, ticketTypeForForm, {settlement_amount_two_decimals: e.target.value});
                  ticketTypeForFormChanged(updated);
                }
              }
            />
          </>
        ) : null}
        {fairMarketValueEnabled && !ticketTypeForForm.free ? (
          <>
            <p className="ticket-drawer-block-title">Fair Market Value</p>
            <TextInput
              as={CurrencyInput}
              id="ticket-type-fair-market-value"
              error={fairMarketValueError(ticketTypeForForm) || undefined}
              label="Value of goods or services per ticket"
              hint="Per table for table seats. Used on donor statements when this show raises money for a campaign."
              leadingText="$"
              maskOptions={{
                prefix: "",
                integerLimit: null
              }}
              value={ticketTypeForForm.fair_market_value_two_decimals || ""}
              onChange={
                (e) => {
                  var updated = Object.assign({}, ticketTypeForForm, {fair_market_value_two_decimals: e.target.value});
                  ticketTypeForFormChanged(updated);
                }
              }
            />
            {parseFloat(ticketTypeForForm.fair_market_value_two_decimals || 0) > 0 ? (
              <TextInput
                id="ticket-type-goods-services-description"
                label="Goods or services provided"
                required
                placeholder="Dinner and drinks"
                error={goodsServicesDescriptionError(ticketTypeForForm) || undefined}
                value={ticketTypeForForm.goods_services_description || ""}
                onChange={
                  (e) => {
                    var updated = Object.assign({}, ticketTypeForForm, {goods_services_description: e.target.value});
                    ticketTypeForFormChanged(updated);
                  }
                }
              />
            ) : null}
          </>
        ) : null}
        {manualTaxes.length > 0 ? (
          <div>
            <p className="ticket-drawer-block-title">Apply Taxes</p>
            {manualTaxes.sort((a, b) => a.name.localeCompare(b.name)).map((manualTax, index) => (
              <div key={index} className="ticket-drawer-check-row">
                <Checkbox
                  id={`manual-tax-${manualTax.id}`}
                  label={`${manualTax.name} (${parseFloat(manualTax.rate)}%)`}
                  checked={manualTaxShouldBeChecked(ticketTypeForForm, manualTax)}
                  onChange={
                    (e) => {
                      var newTaxableItems = [...ticketTypeForForm.taxable_items];
                      var foundIndex = newTaxableItems
                        .map((taxableItem) => taxableItem.manual_tax_id)
                        .indexOf(manualTax.id);

                      if(e.target.checked){
                        if(foundIndex === -1){
                          newTaxableItems.push(
                            {
                              manual_tax_id: manualTax.id,
                              _destroy: false
                            }
                          );
                        } else {
                          newTaxableItems[foundIndex]._destroy = false;
                        }
                      } else if(foundIndex !== -1){
                        newTaxableItems[foundIndex]._destroy = true;
                      }

                      var updated = Object.assign({}, ticketTypeForForm, {
                        taxable_items: newTaxableItems
                      });

                      ticketTypeForFormChanged(updated);
                      debouncedDetermineFinalCosts(updated);
                    }
                  }
                />
              </div>
            ))}
          </div>
        ) : null}
        {!ticketTypeIsSeated(ticketTypeForForm) ? (
          <Checkbox
            id="ticket-type-should-be-default"
            label={
              <LabelWithInfo tip="Default tickets are automatically added to each new event you build.">
                Set as default
              </LabelWithInfo>
            }
            checked={ticketTypeForForm.should_be_default || false}
            onChange={
              (e) => {
                var updated = Object.assign({}, ticketTypeForForm, {should_be_default: e.target.checked});
                ticketTypeForFormChanged(updated);
              }
            }
          />
        ) : null}
        <Checkbox
          id="ticket-type-opendate-distribution"
          label={
            <LabelWithInfo tip="Include this ticket in Open Distribution to broadcast it across partner marketplaces, expanding reach and potential sales.">
              Opendate Distribution
            </LabelWithInfo>
          }
          checked={ticketTypeForForm.opendate_distribution || false}
          onChange={
            (e) => {
              if (e.target.checked && !team.agreed_to_opendate_distribution_terms_at && !distributionTermsAccepted) {
                setShowDistributionTermsModal(true);
                return;
              }
              var updated = Object.assign({}, ticketTypeForForm, {opendate_distribution: e.target.checked});
              ticketTypeForFormChanged(updated);
            }
          }
        />
        <Toggle
          id="ticket-type-manifested"
          label={
            <LabelWithInfo tip="Unmanifested tickets are not counted towards event and venue capacity restrictions and are not shown on artist settlements.">
              {ticketTypeForForm.manifested ? "Manifested Ticket" : "Unmanifested Ticket"}
            </LabelWithInfo>
          }
          checked={ticketTypeForForm.manifested || false}
          onChange={
            (e) => {
              var updated = Object.assign({}, ticketTypeForForm, {manifested: e.target.checked});
              ticketTypeForFormChanged(updated);
            }
          }
        />
        <Select
          id="ticket-type-accessible-seating"
          label={
            <LabelWithInfo tip="Companion tickets are sold alongside an accessible ticket. Accessible and companion tickets can't be resold.">
              Accessible ticket
            </LabelWithInfo>
          }
          options={ACCESSIBLE_TICKET_OPTIONS}
          value={ticketTypeForForm.accessible_seating || ''}
          onChange={accessibleSeating => {
            ticketTypeForFormChanged({
              ...ticketTypeForForm,
              accessible_seating: accessibleSeating
            });
          }}
        />
        {ticketTypeIsSeated(ticketTypeForForm) ? (
          <Toggle
            id="ticket-type-member-presale"
            label={
              <LabelWithInfo tip="Members can buy these seats once their plan's Member Pre-Sale show detail starts, before this ticket type's own on-sale.">
                Members can buy during the Member Pre-Sale
              </LabelWithInfo>
            }
            checked={ticketTypeForForm.member_presale || false}
            onChange={e => {
              ticketTypeForFormChanged({
                ...ticketTypeForForm,
                member_presale: e.target.checked
              });
            }}
          />
        ) : null}
        <Toggle
          id="ticket-type-hide-qr-code"
          label={
            <LabelWithInfo tip="Hide QR codes when you want to sell a ticket that will not need to be scanned for admission or other purposes">
              Hide QR Code
            </LabelWithInfo>
          }
          checked={ticketTypeForForm.hide_qr_code || false}
          onChange={e => {
            ticketTypeForFormChanged({
              ...ticketTypeForForm,
              hide_qr_code: e.target.checked
            });
          }}
        />
        {(team.mode === "AGENT_MANAGER" || ticketTypeForForm.skip_ticket_delivery) && (
          <Toggle
            id="ticket-type-skip-ticket-delivery"
            label={
              <LabelWithInfo tip="Sell this ticket without sending it. It is delivered outside Opendate; the buyer is told their ticket will arrive from the venue closer to the event.">
                Skip ticket delivery
              </LabelWithInfo>
            }
            checked={ticketTypeForForm.skip_ticket_delivery || false}
            onChange={e => {
              ticketTypeForFormChanged({
                ...ticketTypeForForm,
                skip_ticket_delivery: e.target.checked
              });
            }}
          />
        )}
      </form>
    </Drawer>
  );
};

TicketTypeDrawer.propTypes = {
  csrfToken: PropTypes.any,
  team: PropTypes.any,
  confirm: PropTypes.any,
  manualTaxes: PropTypes.any,
  preSaleEnabled: PropTypes.any,
  showActivities: PropTypes.any,
  ticketTypes: PropTypes.any,
  ticketTypeForForm: PropTypes.any,
  ticketTypeForFormChanged: PropTypes.any,
  ticketTypeIsSeated: PropTypes.any,
  exampleTicketChanged: PropTypes.any,
  createTicketType: PropTypes.any,
  updateTicketType: PropTypes.any,
  overCapacity: PropTypes.number,
  raisingCapacity: PropTypes.bool,
  onKeepCapacity: PropTypes.func,
  distributionTermsAccepted: PropTypes.any,
  enablePricePoints: PropTypes.any,
  newPricePoint: PropTypes.any,
  pricePointsMaxDate: PropTypes.any,
  pricePointsMinDate: PropTypes.any,
  setPricePointToRemove: PropTypes.any,
  setShowDistributionTermsModal: PropTypes.any,
  startShowActivity: PropTypes.any,
  useDateAndTime: PropTypes.any,
  useShowDetails: PropTypes.any,
  onAfterOpen: PropTypes.any,
  debouncedDetermineFinalCosts: PropTypes.any,
  closeOnEsc: PropTypes.bool,
};

export default TicketTypeDrawer;

